import React, { useState } from 'react';
import { TutorialCard } from '../components/TutorialCard';
import { SearchIcon, LayoutGridIcon, FileTextIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, SettingsIcon, BarChartIcon, CalendarIcon, TerminalIcon, SmartphoneIcon, BanknoteIcon, GlobeIcon } from 'lucide-react';
// Mock tutorial data
const tutorialData = [{
  id: 1,
  title: 'Getting Started with Payment Requests',
  description: 'Learn how to create and manage payment requests for your customers.',
  category: 'Basics',
  duration: '5 min',
  completed: true,
  completedAt: '2023-11-15T14:30:00',
  icon: <LayoutGridIcon size={24} className="text-blue-500" data-id="element-11965" />
}, {
  id: 2,
  title: 'Setting Up Your Virtual Terminal',
  description: 'Configure your virtual terminal for accepting card payments.',
  category: 'Terminals',
  duration: '8 min',
  completed: true,
  completedAt: '2023-11-20T10:15:00',
  icon: <BanknoteIcon size={24} className="text-purple-500" data-id="element-11966" />
}, {
  id: 3,
  title: 'Creating Your First Invoice',
  description: 'Learn how to create, customize and send professional invoices.',
  category: 'Invoicing',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: <FileTextIcon size={24} className="text-green-500" data-id="element-11967" />
}, {
  id: 4,
  title: 'Managing Customer Profiles',
  description: 'Organize and manage your customer information efficiently.',
  category: 'Customers',
  duration: '4 min',
  completed: true,
  completedAt: '2023-12-01T09:45:00',
  icon: <UsersIcon size={24} className="text-orange-500" data-id="element-11968" />
}, {
  id: 5,
  title: 'Understanding Transaction Reports',
  description: 'Learn how to view and interpret your transaction reports.',
  category: 'Reports',
  duration: '10 min',
  completed: false,
  completedAt: null,
  icon: <BarChartIcon size={24} className="text-red-500" data-id="element-11969" />
}, {
  id: 6,
  title: 'Setting Up Physical Terminals',
  description: 'Configure and manage your physical POS terminals.',
  category: 'Terminals',
  duration: '12 min',
  completed: false,
  completedAt: null,
  icon: <TerminalIcon size={24} className="text-indigo-500" data-id="element-11970" />
}, {
  id: 7,
  title: 'Configuring SoftPOS on Mobile Devices',
  description: 'Turn your smartphone into a payment terminal with SoftPOS.',
  category: 'Terminals',
  duration: '7 min',
  completed: false,
  completedAt: null,
  icon: <SmartphoneIcon size={24} className="text-teal-500" data-id="element-11971" />
}, {
  id: 8,
  title: 'Connecting Third-Party Applications',
  description: 'Integrate your payment system with other business tools.',
  category: 'Integration',
  duration: '15 min',
  completed: true,
  completedAt: '2023-12-10T16:20:00',
  icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-11972" />
}, {
  id: 9,
  title: 'Setting Up Booking Services',
  description: 'Configure your booking system for appointments and reservations.',
  category: 'Bookings',
  duration: '9 min',
  completed: false,
  completedAt: null,
  icon: <CalendarIcon size={24} className="text-pink-500" data-id="element-11973" />
}, {
  id: 10,
  title: 'Managing Your Online Shop',
  description: 'Learn how to set up and manage your online store.',
  category: 'E-commerce',
  duration: '14 min',
  completed: true,
  completedAt: '2023-12-05T11:30:00',
  icon: <ShoppingBagIcon size={24} className="text-yellow-600" data-id="element-11974" />
}, {
  id: 11,
  title: 'Customizing Payment Settings',
  description: 'Configure payment settings to match your business needs.',
  category: 'Settings',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: <SettingsIcon size={24} className="text-gray-600" data-id="element-11975" />
}, {
  id: 12,
  title: 'Advanced Transaction Management',
  description: 'Learn advanced techniques for managing complex transactions.',
  category: 'Advanced',
  duration: '18 min',
  completed: false,
  completedAt: null,
  icon: <ArrowLeftRightIcon size={24} className="text-purple-600" data-id="element-11976" />
}];
// Available categories for filtering
const categories = ['All', 'Basics', 'Terminals', 'Invoicing', 'Customers', 'Reports', 'Integration', 'Bookings', 'E-commerce', 'Settings', 'Advanced'];
export function Tutorials() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'incomplete'
  // Filter tutorials based on search, category, and completion status
  const filteredTutorials = tutorialData.filter(tutorial => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) || tutorial.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || tutorial.category === selectedCategory;
    const matchesFilter = filter === 'all' || filter === 'completed' && tutorial.completed || filter === 'incomplete' && !tutorial.completed;
    return matchesSearch && matchesCategory && matchesFilter;
  });
  // Calculate progress statistics
  const totalTutorials = tutorialData.length;
  const completedTutorials = tutorialData.filter(t => t.completed).length;
  const progressPercentage = Math.round(completedTutorials / totalTutorials * 100);
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-11977">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-11978">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11979">Tutorials</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11980">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11981">
          <div className="text-sm font-medium text-gray-500" data-id="element-11982">
            Total Tutorials
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-11983">
            {totalTutorials}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11984">
          <div className="text-sm font-medium text-gray-500" data-id="element-11985">
            Completed Tutorials
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-11986">
            {completedTutorials}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11987">
          <div className="text-sm font-medium text-gray-500" data-id="element-11988">
            Learning Progress
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-11989">
            {progressPercentage}%
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3" data-id="element-11990">
            <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-500 ease-out" style={{
            width: `${progressPercentage}%`
          }} data-id="element-11991"></div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11992">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-11993">
          {/* Status Filter Tabs */}
          <div className="flex space-x-6" data-id="element-11994">
            <button onClick={() => setFilter('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${filter === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11995">
              All
            </button>
            <button onClick={() => setFilter('completed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${filter === 'completed' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11996">
              Completed
            </button>
            <button onClick={() => setFilter('incomplete')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${filter === 'incomplete' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11997">
              Not Completed
            </button>
          </div>

          {/* Search */}
          <div className="relative w-64" data-id="element-11998">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11999" />
            <input type="text" placeholder="Search tutorials" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors" data-id="element-12000" />
          </div>
        </div>

        {/* Category Pills */}
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 overflow-x-auto" data-id="element-12001">
          <div className="flex space-x-2" data-id="element-12002">
            {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} data-id="element-12003">
                {category}
              </button>)}
          </div>
        </div>

        {/* Tutorials grid */}
        <div className="p-6 bg-gray-50/30" data-id="element-12004">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-12005">
            {filteredTutorials.map(tutorial => <TutorialCard key={tutorial.id} id={tutorial.id} title={tutorial.title} description={tutorial.description} category={tutorial.category} duration={tutorial.duration} completed={tutorial.completed} completedAt={tutorial.completedAt} icon={tutorial.icon} data-id="element-12006" />)}
            {filteredTutorials.length === 0 && <div className="col-span-1 md:col-span-2 lg:col-span-3 py-16 flex flex-col items-center justify-center text-center" data-id="element-12007">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-12008">
                  <SearchIcon size={24} className="text-gray-400" data-id="element-12009" />
                </div>
                <h3 className="text-sm font-medium text-gray-900" data-id="element-12010">
                  No tutorials found
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-12011">
                  Try adjusting your search or filters
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}